import { Add, Delete, Edit } from "@mui/icons-material";
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import { RemoveRedEye } from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TableCell,
  Tooltip,
  Typography,
} from "@mui/material";
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import EmptyContent from "components/EmptyContent";
import List from "components/UI/List";
import { useAlert } from "contexts/alertContext";
import CloseIcon from '@mui/icons-material/Close';
import { Feature } from "models/api";
import React, { useEffect, useState } from "react";
import { QueryClient, useMutation } from "react-query";
import Search from "components/Search/Search";
import { handleError } from "helpers/utils";
import useFetchSection from "hooks/useFetchSection";
import DeleteModal from "components/UI/Form/partials/Delete";
import { InputTypes } from "constants/enums";
import { IFormField } from "models/app";
import { addUser, deleteUser, editUser, getQDetails, getQUsers, getUsers, QUsersCells, QUsersCellsDetails, UsersCells } from "framework/user";
import phone from 'phone';
import { IDropDown, IQUser, IQUserDetails, IUser, IUserInput } from "models/user";
import { use } from "i18next";
import { formatDate } from "helpers";
import { getDropDown } from "framework/services";
import { useNavigate, useParams } from "react-router-dom";
import Image from "components/UI/Image";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";

interface Props {
  feature: Feature;
}


const QUsersDetails: React.FC<Props> = ({ feature }) => {

  const navigate = useNavigate();
  const params = useParams();

  const slugId = params[`${feature.singleName}Id`];

  const [filteredQUsers, setFilterQUsers] = useState<any[]>([])
  const [open, setOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const handleOpen = (img: string) => {
    setSelectedImage(img);
    setOpen(true);
  };


  const handleClose = () => {
    setOpen(false);
    setSelectedImage(null);
  };

  // eslint-disable-next-line
  const queryClient = new QueryClient();


  const {
    loading,
    data: Users,
    error,
    revalidate,
  } = useFetchSection({
    fetchData: () => getQDetails(slugId),
    fetchPermission: async () => null,
    section: "QUsers Details",
    onSuccess: (data) => setFilterQUsers(data.questions),
  });


  if (loading)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );



  const getBodyCells = (row: any) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "questionID")
        return {
          id: "questionID",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.questionID}</Typography>
            </TableCell>
          ),
        };
      if (key === "questionText")
        return {
          id: "questionText",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.questionText}</Typography>
            </TableCell>
          ),
        };

      if (key === "questionDescription")
        return {
          id: "questionDescription",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.questionDescription}</Typography>
            </TableCell>
          ),
        };

      if (key === "answerDate")
        return {
          id: "answerDate",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{formatDate(row?.answerDate)}</Typography>
            </TableCell>
          ),
        };

      if (key === "questionImagePath")
        return {
          id: "questionImagePath",
          element: (
            <TableCell sx={{
              width: '100%',
              overflow: 'hidden',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              bgcolor: '#f5f5f5', // Light gray background
            }} align={"left"} key={i}>
              <Image url={row?.questionImagePath} alt="image" />
            </TableCell>
          ),
        };

      if (key === "answer")
        return {
          id: "answer",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.answer ? 'yes' : 'no'}</Typography>
            </TableCell>
          ),
        }
    });
    return cells;
  };


  const handleSearch = (value: string) => {
    if (!Users) return;

    const searchTerm = value.trim().toLowerCase();

    if (!searchTerm) {
      setFilterQUsers(Users.questions);
      return;
    }

    const filtered = Users.questions.filter(({ answer, questionDescription, questionID, questionText, answerDate }) => {
      const fullText = `${answer} ${questionDescription} ${questionID} ${questionText} ${answerDate}`.toLowerCase();
      return fullText.includes(searchTerm);
    });

    setFilterQUsers(filtered);
  };

  const imageArray = Users?.photos.flatMap((imgObj) =>
    Object.values(imgObj)
  );
  return (
    <>
      <Stack spacing={2} p={2}>
        <Grid
          container
          direction={"row"}
          justifyContent={"space-between"}
          alignItems="center"

        >
          <Stack
            direction={"row"}
            spacing={1}
            justifyContent={"space-between"}
            width={"100%"}
            sx={{ mb: 4 }}
          >
            <Typography
              variant="h3"
              color={"header.primary"}
            >
              {(feature.name)}
            </Typography>


          </Stack>
        </Grid>


        {!!Users!.goal.length && <h1 style={{ marginBlock: 20 }}>Goal</h1>}
        {Users!.goal.map(item =>
          <Card elevation={3} sx={{ width: '100%', maxWidth: 800, mx: 'auto', my: 4, p: 2 }}>
            <CardContent>
              <Stack spacing={3}>
                {/* <Box>
                  <Typography variant="h5" fontWeight="bold" gutterBottom>
                    Goal Question
                  </Typography>
                  <Typography variant="body1">{item.goalQuestion}</Typography>
                </Box> */}

                <Box>
                  <Typography variant="h6" fontWeight="medium">
                    Selected Answer
                  </Typography>
                  <Typography variant="body1">{item.answerText}</Typography>
                </Box>

                <Box>
                  <Typography variant="h6" fontWeight="medium">
                    Comment
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {item.comment || 'No comment provided'}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        )}


        {!!imageArray?.length && <h1 style={{ marginBlock: 20 }}>Photos</h1>}
        <Grid container spacing={2}>
          {imageArray!.map((img) => (
            <Grid item xs={12} sm={6} md={3} key={img}>
              <Card
                onClick={() => handleOpen(img)}
                sx={{
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover .overlay': {
                    opacity: 1,
                  },
                  transition: 'transform 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.02)',
                  },
                }}
              >
                <CardMedia
                  component="img"
                  height="200"
                  image={img}
                  alt={`Teeth ${img}`}
                />
                <Box
                  className="overlay"
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bgcolor: 'rgba(0, 0, 0, 0.4)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    opacity: 0,
                    transition: 'opacity 0.3s ease',
                  }}
                >
                  <ZoomInIcon sx={{ color: 'white', fontSize: 40 }} />
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
        <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
          <DialogContent sx={{ position: 'relative', p: 0, bgcolor: 'black' }}>
            <IconButton
              onClick={handleClose}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                color: 'white',
                zIndex: 1
              }}
            >
              <CloseIcon />
            </IconButton>

            {selectedImage && (
              <Box
                sx={{
                  width: '100%',
                  height: '90vh',
                  overflow: 'hidden',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <TransformWrapper>
                  <TransformComponent>
                    <img
                      src={selectedImage}
                      alt="Expanded Teeth"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '90vh',
                        objectFit: 'contain',
                      }}
                    />
                  </TransformComponent>
                </TransformWrapper>
              </Box>
            )}
          </DialogContent>
        </Dialog>
        <h1 style={{ marginBlock: 20 }}>Questions</h1>
        {filteredQUsers.length > 0 && (
          <List
            headCells={QUsersCellsDetails}
            data={filteredQUsers}
            getBodyCells={getBodyCells}
            feature={feature}
            canEdit={false}
          />
        )}
        {(!filteredQUsers || filteredQUsers.length === 0) &&

          <EmptyContent
            icon={<BlindsClosedIcon sx={{ fontSize: "3.5rem " }} />}
            header={"No EConsultation User Questions"}
            message={`No EConsultation User Questions Exists Yet`}
          />
        }


      </Stack>


    </>
  );
};

export default QUsersDetails;
